import React from 'react';

import { RadioGroupInput } from '@components/Form';
import { TextInput as OldTextInput, TextInputProps } from '@components/Forms/TextInput';
import { TextBox, TextBoxProps } from '@components/TextBox';
import { ClassMap, makeMuiStyles, useCommonStyles, usePalette } from '@styles';
import {
  Field,
  formStrings,
  GoodsField,
  GoodsValue,
  useErrorMessage,
  YesNoField,
  YesNoValue,
} from '@utils/forms';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import { useField, useFormikContext } from 'formik';

const strings = createStrings({
  ja: {
    importTerritory: '主な仕出地',
    exportTerritory: '主な仕向地',
    types: '代表品目',
    amount: '件数／月',
    have: 'あります',
    notHave: 'ありません',
    lastName: '姓',
    firstName: '名',
    existed: 'FC既存顧客',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  label: {
    ...common.middleText,
    ...common.fontWeight500,
    minWidth: 230,
    textAlign: 'left',
  },
  input: {
    ...common.middleText,
  },
  nameTextInput: {},
  required: {
    color: theme.palette.warning.main,
  },
  namePadding: {
    [theme.breakpoints.down('sm')]: {
      ':root &': {
        paddingTop: 55,
      },
    },
    [theme.breakpoints.down('xs')]: {
      ':root &': {
        paddingTop: 0,
      },
    },
  },
  nameSplit: {
    height: 1,
    width: 14,
    backgroundColor: theme.palette.grey['500'],
    margin: '22px 10px',
    [theme.breakpoints.down('sm')]: {
      margin: 0,
    },
  },
  nameSplitContainer: {
    ...common.flexInline,
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      paddingTop: 64,
    },
  },
  checkListItem: {
    ...common.flexColumn,
  },
  checkListQuestion: {
    ...common.middleText,
    fontWeight: 500,
    color: theme.palette.grey['800'],
  },
  radioGroupInput: {
    ...common.flexInline,
    justifyContent: 'space-between',
    padding: '0 1rem',
  },
  formControlLabelRoot: {
    minWidth: '9rem',
  },
  inputPrompt: {
    ...common.middleText,
    color: theme.palette.grey['500'],
    padding: '1rem 0',
  },
  error: {
    ...common.middleText,
    color: theme.palette.error.main,
    paddingTop: '1rem',
  },
  textDisplayContainer: {
    ...common.flexInline,
    ...common.middleText,
    [theme.breakpoints.down('sm')]: {
      ...common.middleText,
      ...common.flexColumn,
    },
    alignItems: 'flex-start',
    padding: '1rem',
    color: theme.palette.text.primary,
    fontWeight: 500,
  },
  textDisplayItem: {
    ...common.flexInline,
    width: '100%',
    maxWidth: 240,
    alignItems: 'center',
    '&$textDisplayItemValue': {
      flex: 1,
      whiteSpace: 'pre-wrap',
    },
  },
  textDisplayItemValue: {
    paddingLeft: '1rem',
    [theme.breakpoints.up('lg')]: {
      paddingLeft: 0,
    },
  },
  columnDisplayContainer: {
    ...common.flexColumn,
    ...common.middleText,
    padding: '1rem',
    color: theme.palette.text.primary,
    fontWeight: 500,
  },
  yesNoDisplayAnswer: {
    padding: '1rem 1.25rem',
  },
  yesNoDisplayPrompt: {
    color: theme.palette.grey['500'],
    padding: '0rem 1.25rem',
  },
  inputTips: {
    ...common.middleText,
    color: theme.palette.grey['500'],
    marginLeft: 246,
    [theme.breakpoints.down('sm')]: {
      marginTop: 5,
      marginLeft: 5,
    },
  },
  inputContainer: {
    position: 'relative',
  },
  checkMark: {
    position: 'absolute',
    top: '11px',
    left: '-35px',
  },
  nameTextInputContainer1: {
    flexBasis: '230px',
    flex: 1,
  },
  nameTextInputContainer2: {
    flex: 1,
  },
}));

export const TextInput: typeof OldTextInput = (props) => {
  const { formClasses, classes: textClasses, required, label, tips, ...rest } = props;

  const classes = useStyles();

  return (
    <>
      <OldTextInput
        label={
          !required ? (
            label
          ) : (
            <>
              {label}
              <span className={classes.required}>{formStrings.required}</span>
            </>
          )
        }
        formClasses={{
          label: classes.label,
          ...formClasses,
        }}
        classes={{
          input: classes.input,
          ...textClasses,
        }}
        {...rest}
      />
      {tips ? <div className={classes.inputTips}>{tips}</div> : null}
    </>
  );
};

export interface NameTextInputProps extends React.ComponentProps<typeof TextInput> {
  firstNameFieldName: string;
}

export const NameTextInput: React.FC<NameTextInputProps> = (props) => {
  const { firstNameFieldName, ...rest } = props;

  const classes = { ...useStyles(), ...useCommonStyles() };

  return (
    <div className={classes.flexInline}>
      <div className={classes.nameTextInputContainer1}>
        <TextInput
          {...rest}
          placeholder={strings.lastName}
          classes={{
            textInput: classes.nameTextInput,
          }}
        />
      </div>

      <div className={classes.nameSplitContainer}>
        <div className={classes.nameSplit} />
      </div>
      <div className={classes.nameTextInputContainer2}>
        <TextInput
          noLabel
          {...rest}
          placeholder={strings.firstName}
          name={firstNameFieldName}
          classes={{
            textInput: classes.nameTextInput,
          }}
        />
      </div>
    </div>
  );
};

export type TextBoxXProps = TextBoxProps & {
  prompt?: string;
  error?: string;
  onSetValue: (v: string | boolean | number) => void;
};

export const TextBoxX: React.FC<TextBoxXProps> = (props) => {
  const { prompt, error, onSetValue, ...rest } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <>
      {prompt ? (
        <div className={classes.inputPrompt}>{prompt}</div>
      ) : (
        <div style={{ height: 12 }} />
      )}
      <TextBox
        setValue={onSetValue}
        classes={{
          input: classes.input,
        }}
        hasError={Boolean(error)}
        {...rest}
      />

      {error && <div className={classes.error}>{error.toString()}</div>}
    </>
  );
};

export interface YesNoInputProps {
  field: YesNoField;
}

export const YesNoInput: React.FC<YesNoInputProps> = (props) => {
  const { field } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [formikField, meta, helpers] = useField(field.name);
  const [reasonField, reasonMeta] = useField(`${field.name}.reason`);

  const inputHasError = !!(reasonMeta.touched && meta.error);
  const shouldDisplayInput = field.reasonWhen === formikField.value.answer;

  const renderInput = () => {
    return (
      <TextBoxX
        prompt={field.prompt}
        onSetValue={(v) =>
          helpers.setValue({
            ...formikField.value,
            reason: v,
          })
        }
        error={inputHasError ? meta.error : undefined}
        placeholder={field.placeholder}
        multiline={field.options.multiline}
        {...reasonField}
      />
    );
  };

  return (
    <div className={classes.checkListItem} id={`FormItem-${field.name}`}>
      <div className={classes.checkListQuestion}>{field.label}</div>

      <div className={classes.flexColumn} style={{ padding: '1rem' }}>
        <RadioGroupInput
          noLabel
          label=""
          name={`${field.name}.answer`}
          iconSize="small"
          radioGroupClasses={{
            root: classes.radioGroupInput,
          }}
          formControlLabelClasses={{
            root: classes.formControlLabelRoot,
            label: classes.middleTextImportant,
          }}
          options={[
            {
              value: 'Y',
              label: field.yes,
            },
            {
              value: 'N',
              label: field.no,
            },
          ]}
        />

        {shouldDisplayInput && renderInput()}
      </div>
    </div>
  );
};

export interface GoodsInputProps {
  field: GoodsField;
  type: 'import' | 'export';
  isDashboard?: boolean;
}

export const GoodsInput: React.FC<GoodsInputProps> = (props) => {
  const { field, type, isDashboard } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };
  const palette = usePalette();

  const [formikField, , helpers] = useField(field.name);

  const [territoryField] = useField(`${field.name}.territory`);
  const territoryErrorMessage = useErrorMessage(`${field.name}.territory`);

  const [typesField] = useField(`${field.name}.types`);
  const typesErrorMessage = useErrorMessage(`${field.name}.types`);

  const [amountField] = useField(`${field.name}.amount`);
  const amountErrorMessage = useErrorMessage(`${field.name}.amount`);
  const context = useFormikContext();
  const select =
    context.submitCount > 0 && !field.options.omittable && formikField.value.illegal === ''
      ? formStrings.shouldSelect
      : undefined;

  return (
    <div className={classes.checkListItem} id={`FormItem-${field.name}`}>
      <div className={classes.checkListQuestion}>
        {field.label}
        <span style={{ color: palette.secondary.main }}>{formStrings.notRequired}</span>
      </div>
      <div className={classes.flexColumn} style={{ padding: '1rem' }}>
        <TextBoxX
          prompt={type === 'import' ? strings.importTerritory : strings.exportTerritory}
          onSetValue={(v) =>
            helpers.setValue({
              ...formikField.value,
              territory: v,
            })
          }
          error={territoryErrorMessage}
          {...territoryField}
        />
        <TextBoxX
          prompt={strings.types}
          onSetValue={(v) =>
            helpers.setValue({
              ...formikField.value,
              types: v,
            })
          }
          error={typesErrorMessage}
          {...typesField}
        />
        <TextBoxX
          prompt={strings.amount}
          onSetValue={(v) =>
            helpers.setValue({
              ...formikField.value,
              amount: v,
            })
          }
          error={amountErrorMessage}
          {...amountField}
        />

        <div className={classes.inputPrompt}>{field.legalQuestion}</div>

        <RadioGroupInput
          noLabel
          label=""
          name={`${field.name}.illegal`}
          iconSize="small"
          radioGroupClasses={{
            root: classes.radioGroupInput,
          }}
          formControlLabelClasses={{
            root: classes.formControlLabelRoot,
            label: classes.middleTextImportant,
          }}
          options={[
            {
              value: 'Y',
              label: strings.have,
            },
            {
              value: 'N',
              label: strings.notHave,
            },
            ...(isDashboard
              ? [
                  {
                    value: 'E',
                    label: strings.existed,
                  },
                ]
              : []),
          ]}
        />

        {select && (
          <div className={classes.error} style={{ paddingLeft: '1rem' }}>
            {select}
          </div>
        )}
      </div>
    </div>
  );
};

export interface MembershipTextInputProps {
  field: Field<any>;
  textInputProps?: Partial<TextInputProps>;
}

export const MembershipTextInput: React.FC<MembershipTextInputProps> = (props) => {
  const { field, textInputProps = {} } = props;

  return (
    <TextInput
      label={field.label}
      name={field.name}
      required={!field.options.omittable}
      placeholder={field.options.placeholder}
      tips={field.options.tips}
      choose={field.options.choose}
      options={field.options.options}
      {...textInputProps}
    />
  );
};

export interface MembershipTextDisplayProps extends MembershipTextInputProps {
  value: any;
  classes?: ClassMap<'container'>;
  label?: React.ReactNode;
}

export const MembershipTextDisplay: React.FC<MembershipTextDisplayProps> = (props) => {
  const { field, value, classes: customClasses = {}, label = field.label } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div className={clsx(classes.textDisplayContainer, customClasses.container)}>
      <div className={clsx(classes.textDisplayItem, classes.textGray700)}>{label}</div>
      <div className={clsx(classes.textDisplayItem, classes.textDisplayItemValue)}>{value}</div>
    </div>
  );
};

export interface MembershipNameTextDisplayProps {
  firstNameField: Field<string>;
  firstName: string;
  lastNameField: Field<string>;
  lastName: string;
  classes?: ClassMap<'container'>;
  label?: React.ReactNode;
}

export const MembershipNameTextDisplay: React.FC<MembershipNameTextDisplayProps> = (props) => {
  const {
    firstNameField,
    firstName,
    lastName,
    classes: customClasses = {},
    label = firstNameField.label,
  } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div className={clsx(classes.textDisplayContainer, customClasses.container)}>
      <div className={clsx(classes.textDisplayItem, classes.textGray700)}>{label}</div>
      <div className={clsx(classes.textDisplayItem, classes.textDisplayItemValue)}>
        {`${lastName} ${firstName}`}
      </div>
    </div>
  );
};

export interface YesNoDisplayProps {
  field: YesNoField;
  value: YesNoValue;
  classes?: ClassMap<'container'>;
}

export const YesNoDisplay: React.FC<YesNoDisplayProps> = (props) => {
  const { field, value, classes: customClasses = {} } = props;

  const classes = useStyles();

  return (
    <div className={clsx(classes.columnDisplayContainer, customClasses.container)}>
      <div>{field.label}</div>

      <div className={classes.yesNoDisplayAnswer}>
        {value.answer === 'Y' ? field.yes : value.answer === 'E' ? strings.existed : field.no}
      </div>

      {value.answer === field.reasonWhen && (
        <>
          <div className={classes.yesNoDisplayPrompt}>{field.prompt}</div>
          <div className={classes.yesNoDisplayAnswer}>{value.reason}</div>
        </>
      )}
    </div>
  );
};

export interface GoodsDisplayProps extends GoodsInputProps {
  value: GoodsValue;
  classes?: ClassMap<'container'>;
}

export const GoodsDisplay: React.FC<GoodsDisplayProps> = (props) => {
  const { field, value, type, classes: customClasses = {} } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div className={clsx(classes.columnDisplayContainer, customClasses.container)}>
      <div>{field.label}</div>

      <div style={{ height: '1rem' }} />

      <div className={classes.verticalSpace1} style={{ paddingLeft: '1.25rem' }}>
        <div className={classes.flexInline}>
          <div style={{ width: '100%', maxWidth: 180 }}>
            {type === 'export' ? strings.exportTerritory : strings.importTerritory}
          </div>
          <div>{value.territory}</div>
        </div>

        <div className={classes.flexInline}>
          <div style={{ width: '100%', maxWidth: 180 }}>{strings.types}</div>
          <div>{value.types}</div>
        </div>

        <div className={classes.flexInline}>
          <div style={{ width: '100%', maxWidth: 180 }}>{strings.amount}</div>
          <div>{value.amount}</div>
        </div>

        <div className={classes.yesNoDisplayPrompt} style={{ paddingLeft: 0 }}>
          {field.legalQuestion}
        </div>

        <div>{value.illegal === 'Y' ? strings.have : strings.notHave}</div>
      </div>
    </div>
  );
};
